import JSZip from 'jszip';
import { paraSlug, slugsUnicos, type PapelAsset } from '@nyroh/contracts';

import { api } from './api';

/**
 * ============================================================================
 * CRIAÇÃO DE SITES EM MASSA — o ZIP da captação vira cliente + site gerado
 * ============================================================================
 *
 * É a irmã de `importacao-massa.ts`, e a diferença entre as duas é a única coisa
 * que importa entender aqui:
 *
 *   IMPORTAR  recebe sites PRONTOS (`index.html` por pasta) e os publica.
 *   CRIAR     recebe LEADS (`dados.json` + fotos por pasta), cadastra o cliente
 *             e manda a IA escrever o site do zero.
 *
 * O FORMATO — é o export do `nyroh-leads`, sem adaptação nenhuma
 *
 *     Eucaglass_Vidros_e_Sacadas_11913058040/
 *       dados.json                  o lead inteiro do Google Places + WhatsApp
 *       foto_2.jpg  foto_3.jpg …    fotos do estabelecimento
 *       foto_perfil_whatsapp.jpg    a foto de perfil (vira a LOGO do site)
 *
 * Nada de novo formato: quem exporta é a tela de leads que a operação já usa, e
 * pedir um formato próprio aqui significaria uma conversão a mais entre duas
 * partes do mesmo sistema.
 *
 * ============================================================================
 * A DIVISÃO DE TRABALHO — por que o navegador faz só três coisas
 * ============================================================================
 *
 *   1. LÊ o ZIP e mostra o que achou. É rápido e é a única chance de conferir
 *      antes de gastar dinheiro: a partir daqui cada site é uma chamada paga à
 *      IA.
 *   2. CADASTRA os clientes (`clientes.importar`, em lotes de 500) e SOBE as
 *      fotos direto para o R2 com URL pré-assinada — os bytes não passam pela
 *      API, mesmo motivo da importação: 5.000 leads × 5 fotos atravessando o
 *      processo Node seriam gigabytes de egress para mover arquivo de um lado
 *      para o outro do mesmo R2.
 *   3. ENFILEIRA um `gerar_site` por cliente e sai da frente.
 *
 * Quem gera é o WORKER, chamando `ia.gerarSite` — que tem a chave, o modelo, a
 * cota, o prompt e a medição de custo. Fechar a aba interrompe só o ENVIO: o que
 * já foi enfileirado continua sendo gerado, e reenviar o mesmo ZIP não duplica
 * nada (o slug do cliente e a chave de idempotência do job seguram).
 *
 * ============================================================================
 * AS FOTOS SOBEM ANTES DO JOB — e a ordem não é detalhe
 * ============================================================================
 *
 * `ia.gerarSite` monta o prompt lendo os assets do cliente: quantas fotos há
 * decide quantos marcadores `[REMOTE_IMG_n]` o modelo pode citar. Enfileirar
 * antes de subir produziria a corrida silenciosa mais cara possível — o worker
 * pega o job, não encontra foto nenhuma, gera um site com imagem de banco, e o
 * lote inteiro sai sem as fotos que estavam ali o tempo todo. Ninguém percebe
 * olhando a fila: os jobs concluem com sucesso.
 */

/** O teto de fotos por cliente que vale a pena subir. */
const MAX_FOTOS_GALERIA = 10;

export interface FotoDoZip {
  nome: string;
  papel: PapelAsset;
  mime: string;
  bytes: number;
  /**
   * Lê os bytes SOB DEMANDA.
   *
   * Mesma razão da importação: um ZIP de 600 leads com cinco fotos cada tem
   * ~400 MB de imagem. Guardar todos os `Blob` na memória para subir um de cada
   * vez é pico de heap por nada — a entrada fica comprimida dentro do JSZip até
   * a hora do PUT, e é descartada logo depois.
   */
  ler: () => Promise<Blob>;
}

/** Uma pasta do ZIP: o cadastro lido do `dados.json` e as fotos ao lado dele. */
export interface LeadDoZip {
  pasta: string;
  /** Slug provisório: `slugsUnicos` reescreve todos no envio, com o lote à vista. */
  slug: string;
  nome: string;
  telefone?: string;
  endereco?: string;
  categoria?: string;
  nota?: number;
  totalAvaliacoes?: number;
  /** O dossiê que vira o prompt do site. Sem ele a IA escreve sobre um genérico. */
  dossie: {
    placeId?: string;
    placeUri?: string;
    website?: string;
    resumo?: string;
    categorias: string[];
    palavrasChaveSeo: string[];
    horarios: unknown;
    avaliacoesTopo: unknown[];
  };
  fotos: FotoDoZip[];
  /** Quantas fotos a pasta tinha além do teto. A tela avisa em vez de sumir com elas. */
  fotosIgnoradas: number;
}

export interface ProblemaNoZip {
  pasta: string;
  motivo: string;
}

const MIME_POR_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  avif: 'image/avif',
  gif: 'image/gif',
};

/**
 * Abre o ZIP e devolve um lead por pasta que tenha `dados.json`.
 *
 * `dados.json` é o único arquivo indispensável — sem ele não há o que cadastrar.
 * Pasta só com fotos é ruído (o export do WhatsApp cria algumas), e entra como
 * problema apenas quando parecia um lead.
 *
 * O agrupamento é pela pasta IMEDIATA do `dados.json`, e não pelo primeiro nível
 * do ZIP: quem compacta a pasta inteira no Windows produz
 * `exemplo_zip_leads/Nome_Telefone/dados.json`, e agrupar pelo topo veria um
 * lead só, com as fotos de todos misturadas.
 */
export async function lerZipDeLeads(
  arquivo: File,
): Promise<{ leads: LeadDoZip[]; problemas: ProblemaNoZip[] }> {
  const zip = await JSZip.loadAsync(arquivo);

  const porPasta = new Map<string, JSZip.JSZipObject[]>();
  zip.forEach((caminho, entrada) => {
    if (entrada.dir) return;
    // __MACOSX/ e ._arquivo aparecem em todo ZIP feito no macOS e não são
    // conteúdo: entrariam como pastas fantasma com um "dados.json" ilegível.
    if (caminho.startsWith('__MACOSX/') || caminho.split('/').pop()?.startsWith('._')) return;
    const partes = caminho.split('/');
    const pasta = partes.length > 1 ? partes.slice(0, -1).join('/') : '';
    const lista = porPasta.get(pasta) ?? [];
    lista.push(entrada);
    porPasta.set(pasta, lista);
  });

  const leads: LeadDoZip[] = [];
  const problemas: ProblemaNoZip[] = [];

  for (const [pasta, entradas] of porPasta) {
    const nomeDe = (e: JSZip.JSZipObject) => e.name.split('/').pop() ?? '';
    const dados = entradas.find((e) => nomeDe(e).toLowerCase() === 'dados.json');
    const nomeDaPasta = (pasta.split('/').pop() || arquivo.name.replace(/\.zip$/i, '')).trim();

    if (!dados) {
      if (entradas.some((e) => nomeDe(e).toLowerCase().endsWith('.json'))) {
        problemas.push({ pasta: pasta || '(raiz)', motivo: 'não tem dados.json' });
      }
      continue;
    }

    let d: Record<string, unknown> | null = null;
    try {
      d = JSON.parse(await dados.async('string')) as Record<string, unknown>;
    } catch (e) {
      console.error(`[criacao] "${dados.name}" não é JSON válido:`, e);
      problemas.push({ pasta: pasta || '(raiz)', motivo: 'dados.json ilegível' });
      continue;
    }

    const nome = texto(d.name) ?? nomeDaPasta;
    if (!nome) {
      problemas.push({ pasta: pasta || '(raiz)', motivo: 'dados.json sem nome do negócio' });
      continue;
    }

    /*
      As fotos, em ordem NUMÉRICA — `foto_2`, `foto_3`, `foto_10`.

      A ordem alfabética colocaria `foto_10` antes de `foto_2`, e ela é a ordem
      em que as fotos entram na galeria do site: a primeira costuma ser a
      fachada. Os números vêm com buracos de propósito no export (a captação só
      grava o que baixou íntegro), então a ordenação é pelo número extraído, não
      pela posição na lista.
    */
    const wa = objeto(d.whatsappData) ?? objeto(d.whatsapp_data);
    const galeria: Array<{ n: number; entrada: JSZip.JSZipObject }> = [];
    let logo: JSZip.JSZipObject | null = null;

    for (const e of entradas) {
      const nomeArquivo = nomeDe(e).toLowerCase();
      const ext = nomeArquivo.split('.').pop() ?? '';
      if (!MIME_POR_EXT[ext]) continue;
      if (nomeArquivo.startsWith('foto_perfil')) {
        logo = e;
        continue;
      }
      const m = /foto_(\d+)/.exec(nomeArquivo);
      galeria.push({ n: m ? Number(m[1]) : Number.MAX_SAFE_INTEGER, entrada: e });
    }
    galeria.sort((a, b) => a.n - b.n);

    const paraFoto = (e: JSZip.JSZipObject, papel: PapelAsset): FotoDoZip => {
      const nomeArquivo = e.name.split('/').pop() ?? 'foto.jpg';
      const ext = nomeArquivo.toLowerCase().split('.').pop() ?? 'jpg';
      return {
        nome: nomeArquivo,
        papel,
        mime: MIME_POR_EXT[ext] ?? 'image/jpeg',
        // Do cabeçalho do ZIP, sem descomprimir: o tamanho é pedido na hora de
        // assinar o upload, e descomprimir tudo só para medir seria pagar o
        // custo inteiro duas vezes.
        bytes:
          (e as unknown as { _data?: { uncompressedSize?: number } })._data?.uncompressedSize ?? 0,
        ler: () => e.async('blob'),
      };
    };

    const fotos: FotoDoZip[] = [
      // A logo primeiro, sempre: `ia.gerarSite` lê os assets do cliente por ordem
      // de chegada e corta no teto. Subindo a foto de perfil por último, uma
      // pasta com muitas fotos empurraria a logo para fora do corte — e o site
      // sairia sem marca no cabeçalho.
      ...(logo ? [paraFoto(logo, 'logo')] : []),
      ...galeria.slice(0, MAX_FOTOS_GALERIA).map((g) => paraFoto(g.entrada, 'galeria')),
    ];

    leads.push({
      pasta: pasta || nomeDaPasta,
      nome,
      slug: paraSlug(nome) || paraSlug(nomeDaPasta),
      ...opcional('telefone', texto(d.formatted_phone_number) ?? texto(d.international_phone_number)),
      ...opcional('endereco', texto(d.formatted_address) ?? texto(d.vicinity)),
      ...opcional('categoria', categoriaDe(d, wa)),
      ...(typeof d.rating === 'number' ? { nota: d.rating } : {}),
      ...(typeof d.user_ratings_total === 'number'
        ? { totalAvaliacoes: d.user_ratings_total }
        : {}),
      dossie: {
        ...opcional('placeId', texto(d.place_id)),
        ...opcional('placeUri', texto(d.url)),
        ...opcional('website', texto(d.website) ?? texto(wa?.businessWebsite)),
        /*
          O resumo sai da DESCRIÇÃO do perfil de WhatsApp do negócio.

          É o único texto do export escrito pelo próprio dono — "envidraçamento
          de sacada, box sob medida, guarda-corpo…" — e é o que faz a IA escrever
          sobre ESTA vidraçaria em vez de sobre vidraçarias. Sem ele o prompt tem
          nome, telefone e categoria, e o site sai intercambiável.
        */
        ...opcional('resumo', texto(wa?.description)),
        categorias: categoriasDe(d, wa),
        // As palavras-chave viram os "serviços" no prompt. Sem lista pronta no
        // export, saem das categorias — melhor que uma seção de serviços vazia.
        palavrasChaveSeo: categoriasDe(d, wa),
        horarios: horariosDe(d),
        avaliacoesTopo: avaliacoesDe(d),
      },
      fotos,
      fotosIgnoradas: Math.max(0, galeria.length - MAX_FOTOS_GALERIA),
    });
  }

  return { leads, problemas };
}

/**
 * A categoria de verdade.
 *
 * `types` do Google traz rótulos ESTRUTURAIS — "establishment",
 * "point_of_interest" — que não descrevem ramo nenhum e apareceriam no site como
 * "Estabelecimento". A categoria do perfil de negócio do WhatsApp ("Glass &
 * Mirror Shop") é escolhida pelo próprio dono e ganha quando existe.
 */
const TIPOS_ESTRUTURAIS = new Set(['establishment', 'point_of_interest', 'store', 'food']);

function categoriaDe(d: Record<string, unknown>, wa: Record<string, unknown> | null): string | undefined {
  return texto(wa?.category) ?? categoriasDe(d, wa)[0];
}

function categoriasDe(d: Record<string, unknown>, wa: Record<string, unknown> | null): string[] {
  const tipos = (Array.isArray(d.types) ? d.types : [])
    .filter((t): t is string => typeof t === 'string' && !TIPOS_ESTRUTURAIS.has(t))
    .map((t) => t.replace(/_/g, ' '));
  const doWa = texto(wa?.category);
  return [...new Set([...(doWa ? [doWa] : []), ...tipos])];
}

/** `weekday_text` é o formato que o prompt já sabe ler. O resto do bloco não serve. */
function horariosDe(d: Record<string, unknown>): unknown {
  const h = objeto(d.opening_hours);
  const texto = h && Array.isArray(h.weekday_text) ? h.weekday_text : null;
  return texto ?? null;
}

/**
 * As avaliações, e SÓ as boas.
 *
 * O prompt as usa como prova social no site do cliente. Uma resenha de duas
 * estrelas — e o export traz — entraria no material que a IA usa para escrever
 * elogios, e o resultado é uma seção de depoimentos citando o problema que o
 * cliente teve. Corte em 4 estrelas, e no máximo cinco.
 */
function avaliacoesDe(d: Record<string, unknown>): unknown[] {
  const lista = Array.isArray(d.reviews) ? d.reviews : [];
  return lista
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
    .filter((r) => typeof r.rating !== 'number' || r.rating >= 4)
    .slice(0, 5)
    .map((r) => ({
      author_name: r.author_name ?? '',
      rating: r.rating ?? null,
      text: typeof r.text === 'string' ? r.text.slice(0, 600) : '',
    }));
}

const texto = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;
const objeto = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const opcional = <K extends string>(k: K, v: string | undefined) =>
  (v ? { [k]: v } : {}) as Record<K, string> | Record<string, never>;

export interface AndamentoCriacao {
  etapa: 'cadastrando' | 'fotos' | 'enfileirando';
  feitos: number;
  total: number;
  /** O lead da vez, para a tela ter o que mostrar. */
  atual: string;
}

export interface ResultadoCriacao {
  clientesCriados: number;
  /** Slug já existia: não é erro, é reenvio. Estes NÃO entram na fila. */
  jaExistiam: number;
  fotosEnviadas: number;
  fotosFalhadas: number;
  enfileirados: number;
  jaNaFila: number;
  falhas: Array<{ pasta: string; motivo: string }>;
}

/**
 * Cadastra os clientes, sobe as fotos e enfileira a geração de cada site.
 *
 * QUEM JÁ EXISTIA NÃO ENTRA NA FILA
 *
 * O slug repetido significa que aquele negócio já está cadastrado — quase sempre
 * porque o mesmo ZIP foi enviado antes. Gerar de novo republicaria o site de um
 * cliente que ninguém pediu para mexer, e cada regeração é uma chamada paga: um
 * reenvio acidental de 5.000 leads custaria o lote inteiro outra vez. A tela diz
 * quantos foram ignorados, e regerar um cliente específico continua sendo um
 * botão na tela dele.
 */
export async function criarSitesEmMassa(
  lote: string,
  leads: LeadDoZip[],
  aoProgredir?: (a: AndamentoCriacao) => void,
): Promise<ResultadoCriacao> {
  const r: ResultadoCriacao = {
    clientesCriados: 0,
    jaExistiam: 0,
    fotosEnviadas: 0,
    fotosFalhadas: 0,
    enfileirados: 0,
    jaNaFila: 0,
    falhas: [],
  };
  if (leads.length === 0) return r;

  /*
    Slugs únicos no LOTE INTEIRO, antes de qualquer envio.

    Duas pastas com nomes parecidos normalizam para o mesmo slug, e slug é UNIQUE
    em `clientes`. Sem desempatar aqui, a segunda cairia no ON CONFLICT DO
    NOTHING do import e seria contada como "já existia" — o negócio simplesmente
    não teria site, sem erro nenhum em tela.
  */
  const slugs = slugsUnicos(leads.map((l) => l.nome || l.pasta));
  const comSlug = leads.map((lead, i) => ({ lead, slug: slugs[i]! }));

  /** Os que nasceram agora — só eles ganham foto e job. */
  const novos: Array<{ lead: LeadDoZip; slug: string; clienteId: string }> = [];

  // Lotes de 500: o teto que o contrato aceita por requisição.
  for (let i = 0; i < comSlug.length; i += 500) {
    const fatia = comSlug.slice(i, i + 500);
    aoProgredir?.({
      etapa: 'cadastrando',
      feitos: i,
      total: comSlug.length,
      atual: fatia[0]?.lead.nome ?? '',
    });

    const importado = await api.chamar('clientes.importar', {
      corpo: {
        clientes: fatia.map(({ lead, slug }) => ({
          nome: lead.nome,
          slug,
          ...(lead.telefone ? { telefone: lead.telefone } : {}),
          ...(lead.endereco ? { endereco: lead.endereco } : {}),
          ...(lead.categoria ? { categoria: lead.categoria } : {}),
          ...(typeof lead.nota === 'number' ? { nota: lead.nota } : {}),
          ...(typeof lead.totalAvaliacoes === 'number'
            ? { totalAvaliacoes: lead.totalAvaliacoes }
            : {}),
          status: 'pendente' as const,
          // O dossiê inteiro: é dele que sai o prompt do site (resumo, horários,
          // avaliações, palavras-chave). Até esta sessão `clientes.importar`
          // descartava este campo em silêncio, e todo cliente importado gerava
          // site com um prompt de nome, categoria e telefone.
          dadosNegocio: lead.dossie,
        })),
      },
    });

    r.clientesCriados += importado.salvos;
    r.jaExistiam += importado.ignorados;

    /*
      O casamento é por SLUG, não por posição.

      `idsCriados` traz só quem nasceu; indexar a lista enviada pela mesma
      posição só funciona quando nenhum slug já existia. Um repetido no meio do
      lote desloca tudo dali para a frente — e o efeito seria as fotos de um lead
      subindo para o cadastro do vizinho, sem erro nenhum.
    */
    const porSlug = new Map(importado.criados.map((c) => [c.slug, c.id]));
    for (const { lead, slug } of fatia) {
      const clienteId = porSlug.get(slug);
      if (clienteId) novos.push({ lead, slug, clienteId });
    }
  }

  // ------------------------------------------------------------------ fotos --
  const comFoto = novos.filter((n) => n.lead.fotos.length > 0);
  for (const [i, { lead, clienteId }] of comFoto.entries()) {
    aoProgredir?.({ etapa: 'fotos', feitos: i, total: comFoto.length, atual: lead.nome });
    for (const foto of lead.fotos) {
      try {
        await enviarFoto(clienteId, foto);
        r.fotosEnviadas++;
      } catch (e) {
        console.error(`[criacao] falha ao subir "${foto.nome}" de ${lead.pasta}:`, e);
        r.fotosFalhadas++;
        /*
          Foto que falha NÃO tira o lead do lote.

          O site continua sendo gerado — com menos fotos, ou com nenhuma. Abortar
          aqui trocaria "um site com quatro fotos em vez de cinco" por "nenhum
          site", que é estritamente pior para quem está esperando o lote.
        */
        r.falhas.push({ pasta: lead.pasta, motivo: `foto ${foto.nome}: ${explicarFalha(e)}` });
      }
    }
  }

  // ------------------------------------------------------------- a geração --
  for (let i = 0; i < novos.length; i += 200) {
    const fatia = novos.slice(i, i + 200);
    aoProgredir?.({
      etapa: 'enfileirando',
      feitos: i,
      total: novos.length,
      atual: fatia[0]?.lead.nome ?? '',
    });

    const enfileirado = await api.chamar('fila.enfileirarLote', {
      corpo: {
        jobs: fatia.map(({ clienteId, slug }) => ({
          tipo: 'gerar_site' as const,
          // Sem `modo`: quem decide IA ou template é o servidor (`config_ia`).
          // Forçar `ia` daqui passaria por cima do interruptor da tela de IA, que
          // é onde a decisão de gastar mora.
          payload: { clienteId, slug, lote },
          // Mesmo cliente, mesma chave: reenviar o ZIP não gera dois jobs — nem
          // duas chamadas pagas ao provedor.
          chaveIdempotencia: `gerar_site:${clienteId}`,
          /*
            SEM `recurso`, pelo mesmo motivo de `esteira-fila.ts`: a fila trata
            "recurso sem cota cadastrada" como ERRO e manda o job para o dead
            letter. Quem consulta a cota do Gemini é a API, dentro de
            `ia.gerarSite`, onde cota estourada vira queda para o template — um
            site publicável — em vez de um lote parado numa fila que ninguém olha.
          */
        })),
      },
    });
    r.enfileirados += enfileirado.enfileirados;
    r.jaNaFila += enfileirado.jaExistiam;
  }

  aoProgredir?.({ etapa: 'enfileirando', feitos: novos.length, total: novos.length, atual: '' });
  return r;
}

/**
 * Um PUT pré-assinado, e o registro depois.
 *
 * Os bytes vão do navegador direto para o R2. A API só assina (decide a chave, o
 * bucket e o prazo) e depois registra a linha — que é o que faz a foto aparecer
 * na biblioteca do cliente e o que `ia.gerarSite` consulta para montar o prompt.
 */
async function enviarFoto(clienteId: string, foto: FotoDoZip): Promise<void> {
  const blob = await foto.ler();
  if (blob.size === 0) throw new Error('arquivo vazio no ZIP');

  const assinatura = await api.chamar('assets.assinarUpload', {
    corpo: {
      nomeArquivo: foto.nome,
      mime: foto.mime,
      // O tamanho REAL do blob, não o do cabeçalho do ZIP: a assinatura tem teto
      // de 20 MB no contrato, e um cabeçalho mentiroso derrubaria o PUT com um
      // erro do R2 em vez de uma mensagem daqui.
      bytes: blob.size,
      clienteId,
    },
  });

  const r = await fetch(assinatura.url, {
    method: 'PUT',
    headers: { 'content-type': foto.mime },
    body: blob,
  });
  if (!r.ok) throw new Error(`o R2 respondeu ${r.status}`);

  await api.chamar('assets.registrar', {
    corpo: {
      chaveR2: assinatura.chaveR2,
      mime: foto.mime,
      bytes: blob.size,
      clienteId,
      papel: foto.papel,
    },
  });
}

/** Mesma tradução da importação: `Failed to fetch` num PUT ao R2 é quase sempre CORS. */
function explicarFalha(e: unknown): string {
  if (e instanceof TypeError) {
    return (
      'o navegador bloqueou o envio para o bucket (CORS ou conexão). ' +
      'Se for CORS: painel da Cloudflare, R2 → bucket de assets → Settings → ' +
      'CORS Policy, permita PUT a partir da origem deste painel. Ver docs/DEPLOY.md §2.1.'
    );
  }
  return e instanceof Error ? e.message : 'falha ao enviar';
}

/** O andamento da geração na fila. Vem do servidor: o worker roda fora desta aba. */
export async function progressoDaGeracao(): Promise<{
  pendente: number;
  processando: number;
  concluido: number;
  falhou: number;
  deadLetter: number;
}> {
  const r = await api.chamar('fila.resumo', {});
  const n = (status: string) => r.porTipoEStatus[`gerar_site:${status}`] ?? 0;
  return {
    pendente: n('pendente'),
    processando: n('processando'),
    concluido: n('concluido'),
    falhou: n('falhou'),
    deadLetter: r.deadLetter,
  };
}

/**
 * O que ESTE lote já gastou de IA, de verdade.
 *
 * Vem de `uso_ia`, somado das linhas que o próprio provedor pagou — uma por
 * chamada, com os tokens que ele reportou. Não é estimativa por site, não é
 * média, e não é contador desta aba: fechar e voltar mostra o gasto real de onde
 * ele estiver.
 */
export async function custoDoLote(lote: string) {
  return api.chamar('ia.resumoCusto', { query: { periodo: 'todos', lote } });
}
